import { useState } from 'react';
import { Link } from 'react-router-dom';
import FaqList from '../components/FaqList';
import Icon from '../components/Icon';
import Layout, { PageHero } from '../components/Layout';
import { ADDRESS_LINE1, ADDRESS_LINE2, COMPANY_NAME, SUPPORT_PHONE } from '../constants/site';
import { openZohoChat } from '../utils/openZohoChat';
import { submitLead } from '../utils/submitLead';

const CONTACT_FAQS = [
  {
    q: 'Is this the publisher’s official support?',
    a: 'No. We are an independent support service, separate from Software MacKiev and Ancestry. The publisher’s official help is available on its own website.',
  },
  {
    q: 'How quickly will you reply?',
    a: 'Live chat is answered as soon as a technician is free, day or night. Form messages usually receive a reply the same day.',
  },
  {
    q: 'What do you do with my details?',
    a: 'We use them only to respond to your support request and never sell them. See our Privacy Policy for full details.',
  },
];

export default function ContactPage() {
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    const form = event.currentTarget;
    const data = new FormData(form);
    const fields = {
      name: String(data.get('name') || '').trim(),
      email: String(data.get('email') || '').trim(),
      phone: String(data.get('phone') || '').trim(),
      message: [data.get('edition') && `Edition: ${data.get('edition')}`, String(data.get('message') || '').trim()]
        .filter(Boolean)
        .join('\n'),
    };

    if (!fields.name || !fields.email || !fields.phone) {
      setError('Please fill in your name, email address, and phone number.');
      return;
    }

    setPending(true);
    try {
      await submitLead({ ...fields, source: 'contact-page', _honey: String(data.get('_honey') || '') });
      setSent(true);
      form.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <Layout>
      <PageHero
        eyebrow="Contact us"
        title="We’re here to help"
        lead="Open live chat for the fastest answer, or send us a message and a technician will get back to you."
        crumbs={[{ label: 'Contact Us' }]}
      />

      <section className="section">
        <div className="container contact-grid">
          <aside className="contact-info">
            <div className="contact-tile contact-tile--feature">
              <Icon name="chat" size={30} />
              <h2>Live chat — 24/7</h2>
              <p>The quickest way to reach a technician. Paste in any error message and we’ll take it from there.</p>
              <button type="button" className="btn btn--accent" onClick={openZohoChat}>
                Start Live Chat
              </button>
            </div>
            {SUPPORT_PHONE ? (
              <div className="contact-tile">
                <Icon name="phone" size={26} />
                <h2>Phone</h2>
                <a href={SUPPORT_PHONE.tel}>{SUPPORT_PHONE.display}</a>
              </div>
            ) : null}
            <div className="contact-tile">
              <Icon name="pin" size={26} />
              <h2>Postal address</h2>
              <p>
                {COMPANY_NAME}
                <br />
                {ADDRESS_LINE1}
                <br />
                {ADDRESS_LINE2}
              </p>
            </div>
            <div className="contact-tile">
              <Icon name="clock" size={26} />
              <h2>Hours</h2>
              <p>Live chat: 24 hours a day, 7 days a week. Form replies: usually same day.</p>
            </div>
          </aside>

          {sent ? (
            <div className="contact-form contact-form--done" role="status">
              <Icon name="check" size={40} />
              <h2>Thank you — message received</h2>
              <p>A technician will be in touch shortly. Need an answer sooner? Live chat is open now.</p>
              <div className="actions">
                <button type="button" className="btn btn--accent" onClick={openZohoChat}>
                  Start Live Chat
                </button>
                <button type="button" className="btn btn--outline" onClick={() => setSent(false)}>
                  Send another message
                </button>
              </div>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <h2>Send us a message</h2>
              <p className="contact-form__hint">Tell us your edition and what is happening — the more detail, the faster we can help.</p>
              <input type="text" name="_honey" className="honey" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <label>
                Your name
                <input name="name" type="text" autoComplete="name" required />
              </label>
              <div className="form-row">
                <label>
                  Email address
                  <input name="email" type="email" autoComplete="email" required />
                </label>
                <label>
                  Phone number
                  <input name="phone" type="tel" autoComplete="tel" required />
                </label>
              </div>
              <label>
                Family Tree Maker edition
                <select name="edition" defaultValue="">
                  <option value="">Not sure</option>
                  <option>Family Tree Maker 2024</option>
                  <option>Family Tree Maker 2019</option>
                  <option>Family Tree Maker 2017</option>
                  <option>Family Tree Maker 2014 or older</option>
                </select>
              </label>
              <label>
                How can we help?
                <textarea
                  name="message"
                  rows={5}
                  placeholder="For example: FTM 2019 on Windows 11 — sync stops at 60% with an error…"
                />
              </label>
              {error ? (
                <p className="form-error" role="alert">
                  {error}
                </p>
              ) : null}
              <button type="submit" className="btn btn--forest btn--block" disabled={pending}>
                {pending ? 'Sending…' : 'Send message'}
              </button>
              <p className="form-legal">
                By sending this form you agree to our <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
                <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>.
              </p>
            </form>
          )}
        </div>
      </section>

      <section className="section section--parchment">
        <div className="container container--narrow">
          <div className="section-head">
            <h2>Before you contact us</h2>
          </div>
          <FaqList items={CONTACT_FAQS} />
        </div>
      </section>
    </Layout>
  );
}
